<template>
  <div class="image-upload-area">
    <div
      :class="[
        'border-2 border-dashed rounded-lg p-6 text-center transition-all duration-200 cursor-pointer',
        isDragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300',
        disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-gray-400'
      ]"
      @drop="handleDrop"
      @dragover="handleDragOver"
      @dragleave="handleDragLeave"
      @click="!disabled && $refs.fileInput.click()"
    >
      <div v-if="!imagePreview" class="space-y-4">
        <div v-if="showIcons" class="text-6xl">📸</div>
        <div class="space-y-2">
          <p class="text-lg font-medium text-gray-700">
            {{ text('title') }}
          </p>
          <p class="text-sm text-gray-500">
            {{ text('pick') }}
          </p>
          <p class="text-xs text-gray-400">
            {{ text('limits') }}
          </p>
        </div>
      </div>

      <div v-else class="space-y-4">
        <div class="relative inline-block">
          <img
            :src="imagePreview.previewUrl"
            alt="תצוגה מקדימה"
            class="max-w-full max-h-48 rounded-lg shadow-md"
          />
        </div>
        <div class="space-y-2">
          <p class="font-medium text-gray-700">
            <span v-if="showIcons">📁 </span>{{ imagePreview.name }}
          </p>
          <p class="text-sm text-gray-500">
            {{ formatFileSize(imagePreview.size) }}
          </p>
          <div class="flex gap-2 justify-center">
            <button
              type="button"
              @click.stop="$refs.fileInput.click()"
              class="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
              :disabled="disabled"
            >
              {{ text('replace') }}
            </button>
            <button
              type="button"
              @click.stop="handleRemoveImage"
              class="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
              :disabled="disabled"
            >
              {{ text('remove') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <input
      ref="fileInput"
      type="file"
      accept="image/*"
      @change="handleFileSelect"
      class="hidden"
      :disabled="disabled"
    />

    <!-- Drag overlay -->
    <div
      v-if="isDragOver"
      class="fixed inset-0 bg-blue-500 bg-opacity-20 flex items-center justify-center z-50 pointer-events-none"
    >
      <div class="bg-white rounded-lg p-6 shadow-lg">
        <div v-if="showIcons" class="text-4xl mb-2">📸</div>
        <p class="text-lg font-medium">{{ text('drop') }}</p>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ImageUploadArea',
  props: {
    currentImage: {
      type: [File, Object],
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    },
    // Jira 8dce4932: callers with their own copy rules (the recruiter Q&A drawer) override the
    // texts and hide the icons; the defaults are the original bug-report texts.
    labels: {
      type: Object,
      default: null
    },
    showIcons: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      isDragOver: false,
      imagePreview: null
    }
  },
  mounted () {
    // Add paste event listener
    document.addEventListener('paste', this.handlePaste)

    // Set initial image if provided
    if (this.currentImage) {
      this.setImagePreview(this.currentImage)
    }
  },
  // Jira 8dce4932: Vue 2 calls beforeDestroy (beforeUnmount is the Vue 3 name and never ran), so
  // the document-wide paste listener used to outlive the component.
  beforeDestroy () {
    // Remove paste event listener
    document.removeEventListener('paste', this.handlePaste)

    // Cleanup preview URL
    if (this.imagePreview && this.imagePreview.previewUrl) {
      URL.revokeObjectURL(this.imagePreview.previewUrl)
    }
  },
  watch: {
    currentImage (newImage) {
      if (newImage) {
        this.setImagePreview(newImage)
      } else {
        this.clearImagePreview()
      }
    }
  },
  methods: {
    text (key) {
      const defaults = {
        title: 'הדבק תמונה (Ctrl+V) או גרור קובץ לכאן',
        pick: 'או לחץ לבחירת קובץ מהמחשב',
        limits: 'קבצי תמונה בלבד, עד 5MB',
        replace: 'החלף',
        remove: 'מחק',
        drop: 'שחרר כדי להעלות תמונה'
      }
      return (this.labels && this.labels[key]) || defaults[key]
    },
    validateFile (file) {
      // Check file type
      if (!file.type.startsWith('image/')) {
        throw new Error('רק קבצי תמונה מותרים')
      }

      // Check file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        throw new Error('גודל הקובץ לא יכול לעלות על 5MB')
      }

      return true
    },

    setImagePreview (file) {
      // Cleanup previous preview URL
      if (this.imagePreview && this.imagePreview.previewUrl) {
        URL.revokeObjectURL(this.imagePreview.previewUrl)
      }

      // Create new preview
      const previewUrl = URL.createObjectURL(file)
      this.imagePreview = {
        file,
        name: file.name,
        size: file.size,
        previewUrl
      }
    },

    clearImagePreview () {
      if (this.imagePreview && this.imagePreview.previewUrl) {
        URL.revokeObjectURL(this.imagePreview.previewUrl)
      }
      this.imagePreview = null
    },

    processImage (file) {
      try {
        this.validateFile(file)
        this.setImagePreview(file)
        this.$emit('image-select', file)
      } catch (error) {
        this.$utils.emit('show_toast', { text: error.message, type: 'error' })
      }
    },

    handlePaste (event) {
      if (this.disabled) return

      const items = event.clipboardData && event.clipboardData.items
      if (!items) return

      for (let item of items) {
        if (item.type.startsWith('image/')) {
          event.preventDefault()
          const file = item.getAsFile()
          if (file) {
            this.processImage(file)
          }
          break
        }
      }
    },

    handleDrop (event) {
      if (this.disabled) return

      event.preventDefault()
      this.isDragOver = false

      const files = event.dataTransfer.files
      if (files.length > 0) {
        this.processImage(files[0])
      }
    },

    handleDragOver (event) {
      if (this.disabled) return
      event.preventDefault()
      this.isDragOver = true
    },

    handleDragLeave (event) {
      if (this.disabled) return
      event.preventDefault()
      this.isDragOver = false
    },

    handleFileSelect (event) {
      if (this.disabled) return

      const file = event.target.files[0]
      if (file) {
        this.processImage(file)
      }
    },

    handleRemoveImage () {
      this.clearImagePreview()
      this.$emit('image-select', null)
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
    },

    formatFileSize (bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    }
  }
}
</script>

<style scoped>
.image-upload-area {
  position: relative;
}

.space-y-4 > * + * {
  margin-top: 1rem;
}

.space-y-2 > * + * {
  margin-top: 0.5rem;
}

.gap-2 {
  gap: 0.5rem;
}

.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 200ms;
}

.duration-200 {
  transition-duration: 200ms;
}

.hover\:border-gray-400:hover {
  border-color: #9ca3af;
}

.hover\:bg-blue-700:hover {
  background-color: #1d4ed8;
}

.hover\:bg-red-700:hover {
  background-color: #b91c1c;
}

.pointer-events-none {
  pointer-events: none;
}

.z-50 {
  z-index: 50;
}

.fixed {
  position: fixed;
}

.inset-0 {
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
}

.flex {
  display: flex;
}

.items-center {
  align-items: center;
}

.justify-center {
  justify-content: center;
}

.bg-blue-500 {
  background-color: #3b82f6;
}

.bg-opacity-20 {
  background-opacity: 0.2;
}

.bg-white {
  background-color: #ffffff;
}

.rounded-lg {
  border-radius: 0.5rem;
}

.shadow-lg {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

.shadow-md {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.p-6 {
  padding: 1.5rem;
}

.px-3 {
  padding-left: 0.75rem;
  padding-right: 0.75rem;
}

.py-1 {
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
}

.text-center {
  text-align: center;
}

.text-6xl {
  font-size: 3.75rem;
  line-height: 1;
}

.text-4xl {
  font-size: 2.25rem;
  line-height: 2.5rem;
}

.text-lg {
  font-size: 1.125rem;
  line-height: 1.75rem;
}

.text-sm {
  font-size: 0.875rem;
  line-height: 1.25rem;
}

.text-xs {
  font-size: 0.75rem;
  line-height: 1rem;
}

.font-medium {
  font-weight: 500;
}

.text-gray-700 {
  color: #374151;
}

.text-gray-500 {
  color: #6b7280;
}

.text-gray-400 {
  color: #9ca3af;
}

.text-white {
  color: #ffffff;
}

.bg-blue-600 {
  background-color: #2563eb;
}

.bg-red-600 {
  background-color: #dc2626;
}

.bg-blue-50 {
  background-color: #eff6ff;
}

.border-2 {
  border-width: 2px;
}

.border-dashed {
  border-style: dashed;
}

.border-blue-500 {
  border-color: #3b82f6;
}

.border-gray-300 {
  border-color: #d1d5db;
}

.cursor-pointer {
  cursor: pointer;
}

.cursor-not-allowed {
  cursor: not-allowed;
}

.opacity-50 {
  opacity: 0.5;
}

.hidden {
  display: none;
}

.relative {
  position: relative;
}

.inline-block {
  display: inline-block;
}

.max-w-full {
  max-width: 100%;
}

.max-h-48 {
  max-height: 12rem;
}

.mb-2 {
  margin-bottom: 0.5rem;
}

.transition-colors {
  transition-property: color, background-color, border-color, text-decoration-color, fill, stroke;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
}
</style>
